// @ts-strict-ignore
import * as React from 'react';
import { Event } from '../../utils/analytics';
import { RootState } from '../../reducers/reducers';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
    selectAllAppNamesAndPlatformsByAppId,
    selectAllAppsIdsInOrg,
    selectOrg,
    selectUser,
    selectIsWorkflowsCIStepEnabled,
    selectAllAppGroupNamesInOrg,
    selectApp,
} from '../../selectors/selectors';
import { selectDestinationsData, selectCanAccessTabularView } from '../../selectors/summaryViewSelectors';
import { getDefaultVersionsForDestinations, getValidVersionForDestinations } from '../../helpers/summaryViewHelpers';
import { OrgComponentProps } from '../../typescriptHelpers';
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    ExclamationCircleOutlined,
    MehTwoTone,
    SendOutlined,
} from '@ant-design/icons';
import {
    antGrey,
    blurryBackdropStyle,
    colorWithAlpha,
    pageContentStyle,
    runwayColors,
    titleStyle,
} from '../styles/globalStyles';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import {
    OrgOverviewStatistic,
    StatisticCategory,
    StatisticScope,
    StatisticTimeSeriesDataPoint,
    StatisticType,
} from '../../types';
import { PageViewType } from '@/types/summaryView';
import Button from 'antd/lib/button';
import Divider from 'antd/lib/divider';
import Typography from 'antd/lib/typography';
import { OrgOverviewEntityRequest, OrgOverviewFiltersV2, tryFetchEntity } from '../../actions/entities';
import { FetchableEntityType } from '../../entityType';
import { useIsLoading } from '@/utils/hooks/helpers';
import useIsRunwayAdmin from '@/utils/hooks/useIsRunwayAdmin';
import { SummaryTable } from '../orgSummary/SummaryTable';
import { useSummaryView } from '@/utils/hooks/useSummaryView';
import { OverviewTableHeader, PageViewContext } from '../orgSummary/OverviewTableHeader';
import { resolveInitialPreferences, createFilterApplyTracker } from '@/helpers/summaryViewHelpers';
import { trackOrgRouteEvent } from '../../actions/trackEvent';
import { flatten, isEqual, pick } from 'lodash';
import { OrgOverviewAppMetricsTabBar, StatisticCategoryOrAll } from './OrgOverviewAppGroupsTabBar';
import { displayTitleForStatCategory } from './orgOverviewHelpers';
import {
    OrgOverviewDurationsBarGraphStat,
    OrgOverviewReleaseStepDurations,
    OrgOverviewStatCard,
    OrgOverviewStatEmptyCard,
    OrgOverviewStatLoadingCard,
} from './OrgOverviewStatCards';
import { EmptyStateWrapper, featureSectionForStatisticCategory } from '../releaseEmptyState/EmptyStateWrapper';

import { useOrgNavigationFilters } from '../sidebar/OrgOverview/OrgNavigationFiltersContext';
import { Skeleton } from 'antd/lib';
import Alert from 'antd/lib/alert/Alert';
import MarkdownComponent from '../HOCs/Markdown';
import MarkdownParagraphWithEllipsis from '../HOCs/MarkdownParagraphWithEllipsis';
import userflow from 'userflow.js';
import Avatar from 'antd/lib/avatar/avatar';
import { useLazyGetOrgInsightsQuery, useLazyGetOrgOverviewV2Query } from '@/middlewares/defaultApi';
import { Link } from 'react-router-dom';
import { UserSettingsPageView } from '../userSettings/UserSettingsComponent';
import { createUserSettingsRoute } from '@/helpers/routeHelpers';
import Layout, { Content } from 'antd/lib/layout/layout';
import Spin from 'antd/lib/spin';

const MIN_STAT_CARD_WIDTH = 288;

const APP_WIDE_STATISTICS: StatisticType[] = [
    StatisticType.ReleaseDuration,
    StatisticType.ItemsCompleted,
    StatisticType.FixRequestsCount,
    StatisticType.FixRequestsApproved,
    StatisticType.FixRequestsRejected,
    StatisticType.PercentWorkItemsInFixRequests,
    StatisticType.WaitingForReviewDuration,
    StatisticType.InReviewDuration,
    StatisticType.CrashFreeSessions,
    StatisticType.CrashFreeUsers,
    StatisticType.BetaCrashFreeSessions,
    StatisticType.BetaCrashFreeUsers,
    StatisticType.AppStoreBuildCount,
    StatisticType.CIWorkflowSuccessPercent,
    StatisticType.CommitCount,
    StatisticType.AppStoreBuildBundleDownloadSize,
    StatisticType.AppStoreBuildBundleInstallSize,
    StatisticType.LinesOfCodeChanged,
    StatisticType.FilesChangedCount,
    StatisticType.StatAverageCustomerRating,
    StatisticType.HotfixReleaseDuration,
    StatisticType.StatAverageTimeToRecover,
    StatisticType.StatFailureRate,
    StatisticType.StatAverageCLIApprovalTime,
    StatisticType.StatAverageReleaseStepCompletionTime,
    StatisticType.StatReleaseFrequency,
    StatisticType.StatReleaseDurations,
    StatisticType.CIAverageBuildTime,
    StatisticType.TotalHotfixesCount,
    StatisticType.StatAverageAppCustomerRating,
];

interface StatCategorySectionProps {
    title?: string;
    isLoading: boolean;
    stats: OrgOverviewStatistic[];
    category: StatisticCategory;
    scope: StatisticScope;
    isWorkflowsCIStepEnabled: boolean;
}

const StatCategorySection: React.FC<StatCategorySectionProps> = ({
    isLoading,
    stats,
    title,
    scope,
    category,
    isWorkflowsCIStepEnabled,
}) => {
    const appNamesAndPlatformsByAppId = useSelector(selectAllAppNamesAndPlatformsByAppId);
    const isEmpty = !isLoading && stats.length === 0;

    return (
        <Space direction="vertical" style={{ width: '100%', marginTop: 30 }} size={20}>
            <div
                style={{
                    display: 'flex',
                    gap: 20,
                    alignItems: 'center',
                }}
            >
                {title && (
                    <>
                        <Typography.Text
                            style={{
                                fontSize: 20,
                                color: antGrey.grey7,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {title}
                        </Typography.Text>
                        <div
                            style={{
                                flex: 1,
                                height: '100%',
                            }}
                        >
                            <Divider
                                type="horizontal"
                                style={{
                                    width: '100%',
                                    margin: 0,
                                }}
                            />
                        </div>
                    </>
                )}
            </div>

            <EmptyStateWrapper
                featureSections={[featureSectionForStatisticCategory(category)]}
                shouldCenterContent
                shouldForceEnable={isEmpty}
                appId={null}
                releaseId={null}
                extraInfo={{
                    scope,
                }}
            >
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(auto-fill, minmax(${MIN_STAT_CARD_WIDTH}px, 1fr))`,
                        gap: 20,
                        flex: 1,
                        gridAutoFlow: 'row',
                    }}
                >
                    {isLoading ?
                        Array(4)
                            .fill(0)
                            .map((_, i) => <OrgOverviewStatLoadingCard key={i} />)
                    : isEmpty ?
                        Array(4)
                            .fill(0)
                            .map((_, i) => <OrgOverviewStatEmptyCard key={i} />)
                    :   stats.map((stat, i) => {
                            if (stat.type === StatisticType.StatAverageReleaseStepCompletionTime) {
                                return (
                                    <OrgOverviewReleaseStepDurations
                                        key={i}
                                        {...stat}
                                        scope={scope}
                                        isWorkflowsCIStepEnabled={isWorkflowsCIStepEnabled}
                                    />
                                );
                            }

                            if (stat.type === StatisticType.StatReleaseDurations) {
                                return <OrgOverviewDurationsBarGraphStat key={i} {...stat} scope={scope} />;
                            }

                            if (stat.type === StatisticType.StatAverageAppCustomerRating) {
                                const { destinationId, destinationName, platform } =
                                    stat.dataPoints?.length ?
                                        (stat.dataPoints[0] as StatisticTimeSeriesDataPoint)
                                    :   ({
                                            destinationId: null,
                                            destinationName: null,
                                            platform: null,
                                        } as StatisticTimeSeriesDataPoint);

                                return (
                                    <OrgOverviewStatCard
                                        key={i}
                                        {...stat}
                                        scope={scope}
                                        appNamesAndPlatformsByAppId={appNamesAndPlatformsByAppId}
                                        shouldDisplayDestination={!!destinationId}
                                        destinationId={destinationId}
                                        destinationName={destinationName}
                                        platform={platform}
                                    />
                                );
                            }

                            return (
                                <OrgOverviewStatCard
                                    key={i}
                                    {...stat}
                                    scope={scope}
                                    appNamesAndPlatformsByAppId={appNamesAndPlatformsByAppId}
                                />
                            );
                        })
                    }
                </div>
            </EmptyStateWrapper>
        </Space>
    );
};

const OrgOverviewV2: React.FC<OrgComponentProps> = (props: OrgComponentProps) => {
    const dispatch = useDispatch();

    const [trackedPageView, setTrackedPageView] = React.useState<string | null>(null);
    const handlePageViewTracking = React.useCallback((pageView: string) => {
        setTrackedPageView((prev) => (prev === pageView ? prev : pageView));
    }, []);

    const [selectedStatCategory, setSelectedStatCategory] = React.useState<StatisticCategoryOrAll>('all');
    const { filters: navigationFilters } = useOrgNavigationFilters();
    const [selectedStatFilters, setSelectedStatFilters] = React.useState<OrgOverviewFiltersV2>({
        orgId: props.orgId,
        appToDestinationIds: navigationFilters.selectedAppToDestinationIds,
        userIds: Object.keys(navigationFilters.selectedUsers),
        selectedMetrics: navigationFilters.selectedMetrics as StatisticType[],
        startDate: navigationFilters.selectedDateRange.start,
        endDate: navigationFilters.selectedDateRange.end,
    });
    const [debouncedSelectedStatFilters, setDebouncedSelectedStatFilters] =
        React.useState<OrgOverviewFiltersV2>(selectedStatFilters);
    const lastSubmittedFiltersRef = React.useRef<OrgOverviewFiltersV2 | null>(null);
    const [fetchOrgOverview, { data: orgOverviewResponse, isLoading, isFetching }] = useLazyGetOrgOverviewV2Query();
    const lastOrgOverviewRequestRef = React.useRef<ReturnType<typeof fetchOrgOverview> | null>(null);
    const [fetchOrgInsights, { data: orgInsightsResponse, isLoading: isLoadingInsights }] =
        useLazyGetOrgInsightsQuery();
    const fetchedInsightsOrgIdsRef = React.useRef(new Set<string>());

    const orgDestinationsData = useSelector((state: RootState) => selectDestinationsData(state, props.orgId));
    const isLoadingOrgDestinations = useIsLoading(FetchableEntityType.OrgDestinationsAndVersions, props.orgId);

    React.useEffect(() => {
        if (!orgDestinationsData && !isLoadingOrgDestinations) {
            dispatch(
                tryFetchEntity(FetchableEntityType.OrgDestinationsAndVersions, { orgId: props.orgId }, props.orgId),
            );
        }
    }, [dispatch, props.orgId, orgDestinationsData, isLoadingOrgDestinations]);

    const { orgId, orgName, orgImageUrl } = useSelector((state: RootState) => {
        const org = selectOrg(state, props.orgId);
        return {
            orgId: org.orgId,
            orgName: org.name,
            orgImageUrl: org.orgImageUrl,
        };
    }, shallowEqual);

    const canAccessTabularView = useSelector((state: RootState) => selectCanAccessTabularView(state, props.orgId));

    // TEST ONLY — remove this block (and the useIsRunwayAdmin import) once tabular view testing is complete.
    // Mirrors the same gate in AppHomeComponent.
    const TABULAR_VIEW_TEST_ORG_ID = 'doordash-storefront';
    const isRunwayAdmin = useIsRunwayAdmin();
    const canSeeTabularView = canAccessTabularView && (orgId !== TABULAR_VIEW_TEST_ORG_ID || isRunwayAdmin);
    // END TEST ONLY

    React.useEffect(() => {
        if (!canSeeTabularView) {
            handlePageViewTracking(PageViewType.Default);
        }
    }, [canSeeTabularView, handlePageViewTracking]);

    const debouncedSelectedStatFilterAppIds = Object.keys(debouncedSelectedStatFilters?.appToDestinationIds ?? {});
    const singleAppId = debouncedSelectedStatFilterAppIds.length === 1 ? debouncedSelectedStatFilterAppIds[0] : null;
    const selectedApp = useSelector((state: RootState) => (singleAppId ? selectApp(state, singleAppId) : null));
    const orgOverview = orgOverviewResponse?.data;

    const statAppIds = Object.keys(navigationFilters.selectedAppToDestinationIds ?? {});
    const isWaitingForInitialOrgOverviewFilters =
        !debouncedSelectedStatFilterAppIds.length && !lastSubmittedFiltersRef.current;
    const isOrgOverviewLoading = isLoading || isFetching || isWaitingForInitialOrgOverviewFilters;
    const overviewImageUrl = selectedApp?.appSettings.appImageUrl ?? orgImageUrl;
    const overviewTitle = selectedApp?.appSettings.appName ?? orgName;

    React.useEffect(() => {
        setSelectedStatFilters({
            orgId: props.orgId,
            appToDestinationIds: navigationFilters.selectedAppToDestinationIds,
            userIds: Object.keys(navigationFilters.selectedUsers),
            selectedMetrics: navigationFilters.selectedMetrics as StatisticType[],
            startDate: navigationFilters.selectedDateRange.start,
            endDate: navigationFilters.selectedDateRange.end,
        });
    }, [
        props.orgId,
        navigationFilters.selectedAppToDestinationIds,
        navigationFilters.selectedDateRange.end,
        navigationFilters.selectedDateRange.start,
        navigationFilters.selectedMetrics,
        navigationFilters.selectedUsers,
    ]);

    React.useEffect(() => {
        if (!trackedPageView) return;
        dispatch(
            trackOrgRouteEvent({
                orgId: props.orgId,
                eventName: Event.ViewOrgOverview,
                viewType: trackedPageView,
            }),
        );
    }, [dispatch, props.orgId, trackedPageView]);

    React.useEffect(() => {
        if (!navigationFilters.shouldGenerateInsights || fetchedInsightsOrgIdsRef.current.has(props.orgId)) {
            return;
        }

        fetchedInsightsOrgIdsRef.current.add(props.orgId);
        void fetchOrgInsights({ orgId: props.orgId }, true);
    }, [fetchOrgInsights, navigationFilters.shouldGenerateInsights, props.orgId]);

    const scope: StatisticScope = StatisticScope.Org;

    const statAppIdsKey = React.useMemo(() => statAppIds.slice().sort().join(','), [statAppIds]);

    React.useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            setDebouncedSelectedStatFilters(selectedStatFilters);
        }, 500);

        return () => window.clearTimeout(timeoutId);
    }, [selectedStatFilters]);

    React.useEffect(() => {
        if (lastSubmittedFiltersRef.current && isEqual(lastSubmittedFiltersRef.current, debouncedSelectedStatFilters)) {
            return;
        }

        if (!debouncedSelectedStatFilterAppIds.length) {
            return;
        }

        const request: OrgOverviewEntityRequest<OrgOverviewFiltersV2> = {
            orgId: props.orgId,
            filters: debouncedSelectedStatFilters,
            appIds: statAppIds,
        };

        if (!request.appIds.length) {
            return;
        }

        lastOrgOverviewRequestRef.current?.abort();
        lastSubmittedFiltersRef.current = debouncedSelectedStatFilters;
        lastOrgOverviewRequestRef.current = fetchOrgOverview(request, true);
    }, [
        debouncedSelectedStatFilterAppIds.length,
        debouncedSelectedStatFilters,
        fetchOrgOverview,
        props.orgId,
        statAppIds,
        statAppIdsKey,
    ]);

    const visibleStatistics = orgOverview?.statistics ?? [];

    const statisticsByType =
        (visibleStatistics.reduce((acc, stat) => {
            acc[stat.type] = [...(acc[stat.type] ?? []), stat];
            return acc;
        }, {}) as Record<string, OrgOverviewStatistic[]>) ?? {};

    const statisticsByCategory = flatten(Object.values(pick(statisticsByType, APP_WIDE_STATISTICS)))
        .sort((a, b) => APP_WIDE_STATISTICS.indexOf(a.type) - APP_WIDE_STATISTICS.indexOf(b.type))
        .filter((statistic) => statistic.category === selectedStatCategory || selectedStatCategory === 'all')
        .reduce(
            (acc, statistic) => ({
                ...acc,
                [statistic.category]: [...(acc[statistic.category] ?? []), statistic],
            }),
            {} as Record<StatisticCategory, OrgOverviewStatistic[]>,
        );

    const allStatCategories = Object.values(StatisticCategory);

    const shouldShowAllCategories = selectedStatCategory === 'all';

    const categoriesToRender =
        shouldShowAllCategories ? allStatCategories : [selectedStatCategory as StatisticCategory];

    const isWorkflowsCIStepEnabled = useSelector((state: RootState) =>
        selectIsWorkflowsCIStepEnabled(state, singleAppId),
    );

    const insightsAlert = orgInsightsResponse?.data;

    const getInsightsAlertType = (): 'success' | 'error' | 'warning' | 'info' => {
        return insightsAlert.type;
    };

    const getInsightsAlertColor = (): string => {
        const alertType = getInsightsAlertType();
        switch (alertType) {
            case 'success':
                return runwayColors.statusGreen;
            case 'error':
                return runwayColors.statusRed;
            case 'warning':
                return runwayColors.statusYellow;
            case 'info':
                return colorWithAlpha(runwayColors.primary, 0.25);
            default:
                return null;
        }
    };

    const getInsightsAlertIcon = (): React.ReactNode => {
        const alertType = getInsightsAlertType();
        const iconStyle = { fontSize: 18 };
        switch (alertType) {
            case 'success':
                return <CheckCircleOutlined style={iconStyle} />;
            case 'error':
                return <CloseCircleOutlined style={iconStyle} />;
            case 'warning':
                return <ExclamationCircleOutlined style={iconStyle} />;
            case 'info':
                return <MehTwoTone style={iconStyle} />;
            default:
                return null;
        }
    };

    const showUserflowInsightsFeedback = () => {
        userflow.start('9bcc579e-33ba-4018-821a-727301341e41');
    };

    const renderChartsView = () => (
        <>
            <div
                style={{
                    position: 'sticky',
                    top: 0,
                    left: 0,
                    padding: '0 32px',
                    marginLeft: -32,
                    paddingTop: 32,
                    zIndex: 2,
                    width: 'calc(100% + 64px)',
                    ...blurryBackdropStyle,
                }}
            >
                <OrgOverviewAppMetricsTabBar
                    selectedStatCategory={selectedStatCategory}
                    onStatCategorySelected={(statCategory) => {
                        setSelectedStatCategory(statCategory);
                    }}
                />
            </div>

            {navigationFilters.shouldGenerateInsights && insightsAlert ?
                <Alert
                    type={getInsightsAlertType()}
                    style={{
                        width: 800,
                        marginTop: 20,
                        padding: 19,
                        border: `1px solid ${getInsightsAlertColor()}`,
                        borderRadius: 0,
                    }}
                    message={
                        isLoadingInsights ?
                            <Skeleton
                                active
                                round={false}
                                style={{
                                    padding: 12,
                                }}
                                paragraph={{
                                    rows: 3,
                                }}
                            />
                        :   <Space style={{ alignItems: 'flex-start' }} size={16}>
                                {getInsightsAlertIcon()}
                                <div>
                                    <MarkdownComponent source={insightsAlert.title} style={{ fontSize: 16 }} />
                                    <MarkdownParagraphWithEllipsis
                                        content={insightsAlert.description}
                                        maxHeight="250px"
                                        style={{ fontSize: 14, marginBottom: 8 }}
                                        backgroundColor="transparent"
                                    />
                                    <Text type="secondary" style={{ marginTop: 8 }}>
                                        AI insights are in beta.{' '}
                                        <Button
                                            type="link"
                                            size="small"
                                            onClick={showUserflowInsightsFeedback}
                                            style={{ padding: 0 }}
                                        >
                                            Send feedback?
                                        </Button>
                                    </Text>
                                </div>
                            </Space>
                    }
                />
            :   null}

            <div
                style={{
                    position: 'relative',
                    marginBottom: 56,
                }}
            >
                {categoriesToRender.map((category) => {
                    const stats: OrgOverviewStatistic[] = statisticsByCategory[category] ?? [];
                    return (
                        <StatCategorySection
                            key={category}
                            category={category}
                            title={shouldShowAllCategories ? displayTitleForStatCategory(category) : null}
                            isLoading={isOrgOverviewLoading}
                            stats={stats}
                            scope={scope}
                            isWorkflowsCIStepEnabled={isWorkflowsCIStepEnabled}
                        />
                    );
                })}
            </div>
        </>
    );

    return (
        <Layout
            style={{
                ...pageContentStyle,
                paddingTop: 0,
                paddingBottom: 0,
            }}
        >
            <Content
                style={{
                    overflowY: 'scroll',
                    overflowX: 'hidden',
                    paddingLeft: 32,
                    paddingRight: 32,
                    position: 'relative',
                }}
            >
                <div
                    style={{
                        zIndex: 1,
                        width: '100%',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            paddingTop: 24,
                        }}
                    >
                        <Avatar src={overviewImageUrl} style={{ marginRight: 20 }} size={80} />
                        <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Text style={{ fontSize: 24, marginBottom: 6, fontWeight: 600, color: antGrey.grey8 }}>
                                    {overviewTitle}
                                </Text>
                                <Link to={createUserSettingsRoute(UserSettingsPageView.Email)}>
                                    <Button icon={<SendOutlined style={{ color: runwayColors.primary }} />}>
                                        Manage subscriptions...
                                    </Button>
                                </Link>
                            </div>
                            <Text style={{ ...titleStyle, color: antGrey.grey6 }}>Overview</Text>
                        </div>
                    </div>
                </div>
                {isLoadingOrgDestinations ?
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                            width: '100%',
                        }}
                    >
                        <Spin size="large" />
                        <Text style={{ color: runwayColors.primary, paddingTop: 10 }}>Loading...</Text>
                    </div>
                : canSeeTabularView ?
                    <div style={{ marginTop: 32 }}>
                        <OrgOverviewWorkflowsContent
                            orgId={orgId}
                            renderChartsView={renderChartsView}
                            onPageViewChange={handlePageViewTracking}
                        />
                    </div>
                :   renderChartsView()}
            </Content>
        </Layout>
    );
};

export default OrgOverviewV2;

interface OrgOverviewWorkflowsContentProps {
    orgId: string;
    renderChartsView: () => React.ReactNode;
    onPageViewChange: (pageView: string) => void;
}

const OrgOverviewWorkflowsContent: React.FC<OrgOverviewWorkflowsContentProps> = ({
    orgId,
    renderChartsView,
    onPageViewChange,
}) => {
    const summary = useSummaryView({ orgId });
    const {
        preferences,
        loading: { isLoading: isLoadingSummaryView, isLoadingInitialData: isLoadingOrgDestinations },
        summaryViewResponse,
        availableVersions,
        availableDestinationIds,
        availableMetrics,
        fetchSummaryViewBatched,
        initializeWithDefaults,
        pagination: { destinationLimit, setLoadedDestinationIds },
        updatePreferences,
    } = summary;

    const showCharts = preferences.pageView === PageViewType.Default;

    const org = useSelector((state: RootState) => selectOrg(state, orgId));
    const user = useSelector((state: RootState) => selectUser(state));
    const appsInOrg = useSelector((state: RootState) => selectAllAppsIdsInOrg(state, orgId));
    const appGroupsInOrg = useSelector((state: RootState) => selectAllAppGroupNamesInOrg(state, orgId));
    const destinationsData = useSelector((state: RootState) => selectDestinationsData(state, orgId));

    const handleTrackFilterApply = React.useMemo(
        () => createFilterApplyTracker(org, user, appsInOrg, appGroupsInOrg, preferences, PageViewContext.OrgOverview),
        [org, user, appsInOrg, appGroupsInOrg, preferences],
    );

    React.useEffect(() => {
        const { pageView, tabularLayout } = preferences;
        const viewType = pageView === PageViewType.Default ? PageViewType.Default : `${pageView}-${tabularLayout}`;
        onPageViewChange(viewType);
    }, [onPageViewChange, preferences.pageView, preferences.tabularLayout]);

    const initialDestinationIds = React.useMemo(() => {
        if (availableDestinationIds.length === 0) return [];
        return destinationLimit ? availableDestinationIds.slice(0, destinationLimit) : availableDestinationIds;
    }, [availableDestinationIds, destinationLimit]);

    const defaultVersions = React.useMemo(() => {
        const validVersion = getValidVersionForDestinations(initialDestinationIds, destinationsData);
        if (validVersion) return [validVersion];
        return getDefaultVersionsForDestinations(availableVersions);
    }, [initialDestinationIds, destinationsData, availableVersions]);

    React.useEffect(() => {
        if (isLoadingOrgDestinations) return;
        if (!destinationLimit) return;
        if (initialDestinationIds.length === 0 || availableVersions.length === 0) return;

        initializeWithDefaults({
            versions: defaultVersions,
            destinationIds: initialDestinationIds,
        });
    }, [
        isLoadingOrgDestinations,
        destinationLimit,
        initialDestinationIds,
        availableVersions.length,
        defaultVersions,
        initializeWithDefaults,
    ]);

    const allMetricKeys = React.useMemo(() => availableMetrics.map((m) => m.key), [availableMetrics]);

    const hasInitialFetchedRef = React.useRef(false);
    React.useEffect(() => {
        if (isLoadingOrgDestinations) return;
        if (!destinationLimit) return;
        if (isLoadingSummaryView) return;
        if (summaryViewResponse) return;
        if (hasInitialFetchedRef.current) return;

        const resolved = resolveInitialPreferences(
            summary.getStoredPreferences(),
            { versions: availableVersions, destinationIds: availableDestinationIds, metricKeys: allMetricKeys },
            { versions: defaultVersions, destinationIds: initialDestinationIds, metrics: allMetricKeys },
        );

        if (resolved.destinationIds.length === 0 || resolved.versions.length === 0) return;

        hasInitialFetchedRef.current = true;

        setLoadedDestinationIds(resolved.destinationIds);
        updatePreferences({ destinations: resolved.destinationIds });
        fetchSummaryViewBatched({
            destinationIds: resolved.destinationIds,
            versions: resolved.versions,
            properties: resolved.metrics,
        });
    }, [
        isLoadingOrgDestinations,
        isLoadingSummaryView,
        summaryViewResponse,
        availableDestinationIds,
        availableVersions,
        allMetricKeys,
        initialDestinationIds,
        defaultVersions,
        destinationLimit,
        summary.getStoredPreferences,
        fetchSummaryViewBatched,
        setLoadedDestinationIds,
        updatePreferences,
    ]);

    return (
        <>
            <OverviewTableHeader
                context={PageViewContext.OrgOverview}
                summary={summary}
                onTrackFilterApply={handleTrackFilterApply}
            />
            {showCharts ? renderChartsView() : <SummaryTable summary={summary} />}
        </>
    );
};
